import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { storePayoutsApi } from '../services/api';
import PayoutActionDialog from './PayoutActionDialog';
import PayoutSettingsCard from './PayoutSettingsCard';
import {
  ACTION_COPY,
  FLAG_FILTERS,
  FLAG_HINTS,
  FLAG_LABELS,
  HOLD_REASON_LABELS,
  PAYOUT_STATUS_COLORS,
  PAYOUT_STATUS_LABELS,
  STATUS_FILTERS,
  errorText,
  formatDateTime,
  formatMoney,
} from './storePayoutsUtils';

const PAGE_SIZE = 50;
const ACTIONS = [
  { action: 'release', can: 'canRelease', variant: 'contained' },
  { action: 'hold', can: 'canHold', variant: 'outlined' },
  { action: 'resume', can: 'canResume', variant: 'outlined' },
  { action: 'retry', can: 'canRetry', variant: 'contained' },
];

const Amount = ({ label, value, currency, strong }) => (
  <Typography variant="body2" sx={strong ? { fontWeight: 600 } : undefined}>
    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>{label}: </Box>
    {formatMoney(value, currency)}
  </Typography>
);

const PayoutRow = ({ p, onAction }) => {
  const flagged = (p.flags || []).length > 0;
  const failing = p.status === 'FAILED';
  const cur = p.currencyCode;
  return (
    <TableRow
      hover
      data-testid={`payout-row-${p.orderId}`}
      data-flagged={flagged ? 'true' : 'false'}
      sx={{
        verticalAlign: 'top',
        ...(flagged && { backgroundColor: 'warning.light', '&:hover': { backgroundColor: 'warning.light' } }),
      }}
    >
      <TableCell>
        <Link component={RouterLink} to={`/orders/${p.orderId}`}>Order #{p.orderId}</Link>
        <Typography variant="body2">{p.storeName || '—'} to {p.buyerName || '—'}</Typography>
        {p.storeId != null && (
          <Typography variant="caption" color="text.secondary">Store #{p.storeId}</Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={PAYOUT_STATUS_COLORS[p.status] || 'default'}
          label={PAYOUT_STATUS_LABELS[p.status] || p.status}
        />
        {(p.holdReasons || []).map((r) => (
          <Box key={r}>
            <Chip size="small" variant="outlined" color="warning" label={HOLD_REASON_LABELS[r] || r} sx={{ mt: 0.5 }} />
          </Box>
        ))}
        {failing && (
          <Alert severity="warning" icon={<WarningIcon fontSize="inherit" />} sx={{ mt: 0.5, py: 0 }}>
            {p.attempts > 0 ? `${p.attempts} failed attempt${p.attempts === 1 ? '' : 's'}` : 'Release failed'}
            {p.lastError ? `: ${p.lastError}` : ''}
            {p.nextAttemptAt ? ` (next try ${formatDateTime(p.nextAttemptAt)})` : ''}
          </Alert>
        )}
        {!failing && p.lastError && (
          <Alert
            severity="warning"
            data-testid={`payout-last-error-${p.orderId}`}
            sx={{ mt: 0.5, py: 0 }}
          >
            {(p.flags || []).includes('RELEASE_UNCONFIRMED')
              ? `Last error (use it to check PayPal): ${p.lastError}`
              : `Last error: ${p.lastError}`}
          </Alert>
        )}
        {(p.flags || []).map((f) => (
          <Box key={f}>
            <Tooltip title={FLAG_HINTS[f] || f}>
              <Chip
                size="small"
                color="warning"
                icon={<WarningIcon />}
                label={FLAG_LABELS[f] || f}
                sx={{ mt: 0.5, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal' } }}
              />
            </Tooltip>
          </Box>
        ))}
      </TableCell>
      <TableCell>
        <Amount label="Goods" value={p.goodsAmount} currency={cur} />
        <Amount label="Delivery" value={p.deliveryAmount} currency={cur} />
        <Amount label="Order total" value={p.orderTotal} currency={cur} />
        {Number(p.refundedAmount) > 0 && <Amount label="Refunded" value={p.refundedAmount} currency={cur} />}
        <Amount label="Commission" value={p.commissionAmount} currency={cur} />
        {Number(p.commissionRefundedAmount) > 0 && (
          <Amount label="Commission refunded" value={p.commissionRefundedAmount} currency={cur} />
        )}
        <Amount label="Store gets" value={p.storeAmount} currency={cur} strong />
      </TableCell>
      <TableCell>
        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Delivered: </Box>{formatDateTime(p.deliveredAt)}</Typography>
        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Due: </Box>{formatDateTime(p.dueAt)}</Typography>
        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Hard deadline: </Box>{formatDateTime(p.hardDeadlineAt)}</Typography>
        {p.releasedAt && (
          <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Released: </Box>{formatDateTime(p.releasedAt)}</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'stretch' }}>
          {ACTIONS.filter((a) => p[a.can]).map((a) => (
            <Button
              key={a.action}
              size="small"
              variant={a.variant}
              onClick={() => onAction(p, a.action)}
              aria-label={`${ACTION_COPY[a.action].confirm} for order ${p.orderId}`}
            >
              {ACTION_COPY[a.action].confirm.replace(' payout', '')}
            </Button>
          ))}
        </Box>
      </TableCell>
    </TableRow>
  );
};

/** Admin page: held store payouts (release / hold / resume / retry) and the payout settings. */
const StorePayouts = () => {
  const [status, setStatus] = useState('');
  const [flag, setFlag] = useState('');
  const [storeInput, setStoreInput] = useState('');
  const [storeId, setStoreId] = useState('');
  const [page, setPage] = useState(0);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(null);
  const [success, setSuccess] = useState('');

  useEffect(() => { document.title = 'Store payouts'; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await storePayoutsApi.list({
        statuses: status ? [status] : [],
        providerId: storeId,
        flag,
        page,
        size: PAGE_SIZE,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems(null);
      setError(errorText(err, 'Failed to load payouts'));
    } finally {
      setLoading(false);
    }
  }, [status, storeId, flag, page]);

  useEffect(() => { load(); }, [load]);

  const storeInvalid = storeInput.trim() !== '' && !/^\d+$/.test(storeInput.trim());

  const applyStore = () => {
    if (storeInvalid) return;
    setStoreId(storeInput.trim());
    setPage(0);
  };

  const handleDone = (result, action) => {
    const orderId = acting?.orderId;
    setActing(null);
    if (result) setSuccess(`Order #${orderId} payout ${ACTION_COPY[action].verb}.`);
    load();
  };

  const filtered = !!(status || flag || storeId);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom>Store payouts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The money for a store order is held and paid to the store after the return window. Release it
        early, pause it, resume it, or retry a payout that failed. At the hard deadline everything
        unfinished is paid anyway.
      </Typography>

      <PayoutSettingsCard />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Select
          size="small"
          displayEmpty
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Payout status filter' }}
          sx={{ minWidth: 200 }}
        >
          {STATUS_FILTERS.map((f) => <MenuItem key={f.value || 'all'} value={f.value}>{f.label}</MenuItem>)}
        </Select>
        <Select
          size="small"
          displayEmpty
          value={flag}
          onChange={(e) => { setFlag(e.target.value); setPage(0); }}
          inputProps={{ 'aria-label': 'Payout flag filter' }}
          sx={{ minWidth: 260 }}
        >
          {FLAG_FILTERS.map((f) => <MenuItem key={f.value || 'any'} value={f.value}>{f.label}</MenuItem>)}
        </Select>
        <TextField
          size="small"
          label="Store ID"
          value={storeInput}
          onChange={(e) => setStoreInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyStore(); }}
          onBlur={applyStore}
          error={storeInvalid}
          helperText={storeInvalid ? 'Enter a numeric store id.' : ' '}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ width: 140 }}
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading payouts" />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && items && items.length === 0 && (
        <Alert severity="info">
          {page > 0
            ? 'No more payouts on this page.'
            : filtered ? 'No payouts match these filters.' : 'There are no store payouts yet.'}
        </Alert>
      )}

      {!loading && !error && items && items.length === 0 && page > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
          <Button size="small" onClick={() => setPage((n) => n - 1)}>Previous</Button>
        </Box>
      )}

      {!loading && !error && items && items.length > 0 && (
        <Paper>
          <TableContainer>
            <Table size="small" aria-label="Store payouts">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amounts</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((p) => <PayoutRow key={p.orderId} p={p} onAction={(row, action) => setActing({ ...row, action })} />)}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 1 }}>
            <Button size="small" disabled={page === 0} onClick={() => setPage((n) => n - 1)}>Previous</Button>
            <Button size="small" disabled={items.length < PAGE_SIZE} onClick={() => setPage((n) => n + 1)}>Next</Button>
          </Box>
        </Paper>
      )}

      {acting && (
        <PayoutActionDialog
          key={`${acting.orderId}-${acting.action}`}
          payout={acting}
          action={acting.action}
          onClose={() => setActing(null)}
          onDone={handleDone}
        />
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={8000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
    </Container>
  );
};

export default StorePayouts;
